import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Finding, FindingsPayload, Freshness, MergeReadiness} from './types'

interface UseFindingsOptions {
  pullRequestBasePath: string
}

interface UseFindingsResult {
  findings: Finding[]
  ignoredEntityIds: string[]
  freshness: Freshness
  mergeReadiness: MergeReadiness | null
  overviewSummary: string | null
  modelsUrl: string | null
  isLoading: boolean
  error: string | null
}

const DEFAULT_RETRY_AFTER_SECONDS = 10
const MIN_RETRY_AFTER_SECONDS = 1

const getRetryAfterMs = (retryAfterHeader: string | null): number => {
  const parsedRetryAfter = retryAfterHeader === null ? Number.NaN : Number(retryAfterHeader)
  const retryAfterSeconds = Number.isFinite(parsedRetryAfter)
    ? Math.max(MIN_RETRY_AFTER_SECONDS, parsedRetryAfter)
    : DEFAULT_RETRY_AFTER_SECONDS

  return retryAfterSeconds * 1000
}

interface FindingsQueryData {
  payload: FindingsPayload
  retryAfterMs: number
}

export function useFindings({pullRequestBasePath}: UseFindingsOptions): UseFindingsResult {
  const {data, isLoading, error} = useQuery({
    queryKey: ['code-review-findings', pullRequestBasePath],
    queryFn: async (): Promise<FindingsQueryData> => {
      const response = await verifiedFetchJSON(`${pullRequestBasePath}/code_review_findings`)
      if (!response.ok) {
        throw new Error(`Failed to fetch findings (${response.status})`)
      }
      const payload = (await response.json()) as FindingsPayload
      return {
        payload,
        retryAfterMs: getRetryAfterMs(response.headers.get('Retry-After')),
      }
    },
    refetchInterval: query => {
      const freshness = query.state.data?.payload.freshness
      if (freshness && freshness !== 'current') {
        return query.state.data?.retryAfterMs ?? DEFAULT_RETRY_AFTER_SECONDS * 1000
      }
      return false
    },
  })

  const payload = data?.payload

  return {
    findings: payload?.findings ?? [],
    ignoredEntityIds: payload?.ignoredEntityIds ?? [],
    freshness: payload?.freshness ?? 'pending',
    mergeReadiness: payload?.mergeReadiness ?? null,
    overviewSummary: payload?.overviewSummary ?? null,
    modelsUrl: payload?.modelsUrl ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch findings') : null,
  }
}
